import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { getCurrentChatUser, listDirectoryUsers } from '../../../services/chat-service'
import { findUpcomingOnCallReminder, formatOnCallWindow } from '../on-call-service'
import type { OnCallPeriod } from '@shared/on-call/types'

const ACK_KEY = 'ascend-on-call-reminder-ack'

function readAcked(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(ACK_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function ackPeriod(periodId: string): void {
  const ids = readAcked()
  ids.add(periodId)
  const trimmed = [...ids].slice(-50)
  localStorage.setItem(ACK_KEY, JSON.stringify(trimmed))
}

export function useOnCallReminders(): {
  reminder: OnCallPeriod | null
  dismissReminder: () => void
} {
  const { user } = useAuth()
  const [reminder, setReminder] = useState<OnCallPeriod | null>(null)

  const check = useCallback(async () => {
    if (!user?.userId) return
    try {
      const [me, directory] = await Promise.all([getCurrentChatUser(), listDirectoryUsers()])
      const roster = [...directory, me]
      const upcoming = findUpcomingOnCallReminder(me.userId, roster)
      if (!upcoming) {
        setReminder(null)
        return
      }
      if (readAcked().has(upcoming.id)) {
        setReminder(null)
        return
      }
      setReminder(upcoming)

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const tag = `on-call-${upcoming.id}`
        const existing = await navigator.serviceWorker?.getRegistration().then((reg) =>
          reg?.getNotifications({ tag }),
        )
        if (!existing?.length) {
          new Notification('On-call starting soon', {
            body: `Your on-call period begins at ${new Date(upcoming.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
            tag,
          })
        }
      }
    } catch {
      /* best effort */
    }
  }, [user?.userId])

  useEffect(() => {
    void check()
    const id = setInterval(() => void check(), 60_000)
    return () => clearInterval(id)
  }, [check])

  const dismissReminder = useCallback(() => {
    if (reminder) ackPeriod(reminder.id)
    setReminder(null)
  }, [reminder])

  return { reminder, dismissReminder }
}

export function OnCallReminderBanner({
  reminder,
  onDismiss,
}: {
  reminder: OnCallPeriod
  onDismiss: () => void
}) {
  return (
    <div
      className="fixed inset-x-0 top-0 z-[55] flex items-center justify-center px-4 pt-[max(0.5rem,var(--safe-top))]"
      role="status"
      aria-live="polite"
    >
      <div className="flex w-full max-w-lg items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 shadow-lg">
        <span className="text-lg" aria-hidden>
          🟠
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-orange-900">On-call starting soon</p>
          <p className="truncate text-xs text-orange-800">
            {formatOnCallWindow(reminder.startTime, reminder.endTime)}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700"
        >
          Got it
        </button>
      </div>
    </div>
  )
}
